import { Button, Chip, Icon, Input, Label } from "@st-marketplace/ui";
import {
	type ChangeEvent,
	type ClipboardEvent,
	useEffect,
	useId,
	useState,
} from "react";

interface ChipInputProps {
	chips?: string[];
	defaultChips?: string[];
	onChipsChange?: (chips: string[]) => void;
	label?: string;
	disabled?: boolean;
	error?: string;
	getRemoveLabel?: (chip: string) => string;
}

const CODE_LENGTH = 8;

const ChipInput: React.FC<ChipInputProps> = ({
	chips: controlledChips,
	defaultChips = [],
	onChipsChange,
	label,
	disabled,
	error,
	getRemoveLabel = (chip) => `Remove ${chip}`,
}) => {
	const inputId = useId();
	const isControlled = Array.isArray(controlledChips);
	const [internalChips, setInternalChips] = useState<string[]>(defaultChips);
	const [inputValue, setInputValue] = useState("");

	const chipList = isControlled ? controlledChips ?? [] : internalChips;

	useEffect(() => {
		if (!isControlled) setInternalChips(defaultChips);
	}, [defaultChips, isControlled]);

	const updateChips = (newChips: string[]) => {
		if (isControlled) {
			onChipsChange?.(newChips);
		} else {
			setInternalChips(newChips);
			onChipsChange?.(newChips);
		}
	};

	const isValidChip = (val: string) =>
		val.length === CODE_LENGTH && /^\d+$/.test(val) && !chipList.includes(val);

	const addChips = (values: string[]) => {
		const newChips = values.filter(isValidChip);
		if (newChips.length) {
			updateChips([...chipList, ...newChips]);
		}
	};

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const sanitized = e.target.value.replace(/\D+/g, "").slice(0, CODE_LENGTH);

		if (sanitized.length === CODE_LENGTH) {
			addChips([sanitized]);
			setInputValue("");
			return;
		}

		setInputValue(sanitized);
	};

	const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();

		const pastedText = e.clipboardData.getData("text");
		const digitsOnly = pastedText.replace(/\D+/g, "");

		const chunks: string[] = [];
		for (let i = 0; i + CODE_LENGTH <= digitsOnly.length; i += CODE_LENGTH) {
			chunks.push(digitsOnly.slice(i, i + CODE_LENGTH));
		}

		addChips(chunks);
		const remainder = digitsOnly.slice(chunks.length * CODE_LENGTH);
		setInputValue(remainder);
	};

	return (
		<div className="mb-4 min-w-0 max-w-full">
			{label && (
				<Label className="mb-1" htmlFor={inputId}>
					{label}
				</Label>
			)}
			<Input
				id={inputId}
				type="text"
				inputMode="numeric"
				value={inputValue}
				onChange={handleInputChange}
				onPaste={handlePaste}
				disabled={disabled}
				validation={error ? "invalid" : "none"}
			/>
			{error && (
				<p
					role="alert"
					className="mt-1 flex items-center gap-1 text-xs text-danger-text"
				>
					<Icon name="error-warning-line" size="xs" />
					{error}
				</p>
			)}

			<div className="flex min-w-0 max-w-full flex-wrap gap-2 pt-2">
				{chipList.map((chip) => (
					<span
						key={chip}
						className="inline-flex max-w-full items-center gap-1"
					>
						<Chip variant="muted" size="sm" className="pe-1">
							{chip}
							<Button
								type="button"
								icon
								color="ghost"
								size="sm"
								className="ms-1 rounded-full"
								aria-label={getRemoveLabel(chip)}
								onClick={() => updateChips(chipList.filter((c) => c !== chip))}
							>
								<Icon name="close-line" size="xs" />
							</Button>
						</Chip>
					</span>
				))}
			</div>
		</div>
	);
};

export default ChipInput;
